import { captureEvent, captureException, Severity } from '@sentry/browser';
import { useFlags } from 'launchdarkly-react-client-sdk';
import flatten from 'lodash/flatten';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { appClient } from 'src/config/apollo';
import { Dispatch } from 'src/config/rootStore';
import { Assessment } from 'src/modules/assessments/components/Assessment';
import { Who5Assessment } from 'src/modules/assessments/components/Who5Assessment';
import {
    AssessmentType,
    FlowType,
    MappedAnswers,
    Question,
} from 'src/modules/assessments/models/assessments';
import { getAssessments } from 'src/modules/assessments/selectors/assessmentsSelectors';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';

import { useStartAssessmentFlowMutation } from '../documents/startAssessmentFlowMutation.generated';

export interface Scores {
    previousWho5: number;
    [AssessmentType.Who5]: number;
    [AssessmentType.Phq2]?: number;
    [AssessmentType.Phq8]?: number;
    [AssessmentType.Phq9]?: number;
    [AssessmentType.Gad2]?: number;
    [AssessmentType.Gad7]?: number;
}
interface Props {
    onWho5Submit?: (newScore: number, oldScore: number) => AssessmentType[] | null;
    onSubmit: (scores: Scores) => void;
    flowType: FlowType;
    startAssessmentFlowVariables?: Record<string, string>;
    currentWeekForLegacyPrograms?: number;
    hideEmploymentHealthQuestions?: boolean;
    showTopControls?: boolean;
    onlyWho5?: boolean;
    showFullAssessment?: boolean;
}

export const SetOfAssessments: React.FC<Props> = ({
    onSubmit,
    onWho5Submit,
    flowType,
    onlyWho5 = false,
    showTopControls = false,
    currentWeekForLegacyPrograms,
    hideEmploymentHealthQuestions,
    startAssessmentFlowVariables = {},
    showFullAssessment = false,
}) => {
    const flags = useFlags();

    // High score means who5 > who5ScoreThresholdForTherapy feature flag
    const highWellbeingAssessments = [AssessmentType.Gad2, AssessmentType.Phq2];
    // Old will be used if `hidePhq8Gad7Assessments` feature flag is true
    const highWellbeingAssessmentsOld = [];

    // Low score means who5 <= who5ScoreThresholdForTherapy feature flag
    const lowWellbeingAssessments = [
        AssessmentType.Gad7,
        flags.hidePhq9QuestionsWeb ? AssessmentType.Phq8 : AssessmentType.Phq9,
    ]; // Old will be used if `hidePhq8Gad7Assessments` feature flag is true
    const lowWellbeingAssessmentsOld = [AssessmentType.Gad2, AssessmentType.Phq2];

    const { coveredTherapyVisits } = useSelector(getSessionUtilizationInfo);
    const availableAssessments = useSelector(getAssessments);

    const dispatch: Dispatch = useDispatch();

    const [startAssessmentFlow] = useStartAssessmentFlowMutation({
        client: appClient,
        fetchPolicy: 'no-cache',
    });

    const [assessmentFlowUuid, setAssessmentFlowUUid] = useState('');
    const [postWho5Questions, setPostWho5Questions] = useState<Question[]>([]);
    const [scores, setScores] = useState({});

    useEffect(() => {
        startAssessmentFlow({ variables: { flowType, ...startAssessmentFlowVariables } })
            .then(response => {
                const uuid = response?.data?.startAssessmentFlow?.assessmentFlow?.uuid;

                if (!uuid) {
                    captureEvent({
                        message: 'We have not received correct assessment flow uuid in a response',
                        level: Severity.Error,
                        extra: { uuid },
                    });
                } else {
                    setAssessmentFlowUUid(uuid);
                }
            })
            .catch(captureException);
    }, []);

    const retrieveQuestions = (assessments: AssessmentType[]) => {
        const questions = assessments.map(
            assessment => availableAssessments[assessment]?.assessmentQuestions || [],
        );

        setPostWho5Questions(flatten(questions));
    };

    const handleWho5AssessmentSubmit = (newScore: number, oldScore: number) => {
        let assessments;

        // call onWho5Submit if received in props and get assessments overwrite in return
        if (onWho5Submit) {
            assessments = onWho5Submit(newScore, oldScore);
        }

        if (!assessments) {
            // use default logic if no overwrite received from `onWho5Submit`

            if (showFullAssessment) {
                assessments = flags?.hidePhq8Gad7Assessments
                    ? lowWellbeingAssessmentsOld
                    : lowWellbeingAssessments;
            } else {
                const qualifiesForTherapy =
                    (coveredTherapyVisits > 0 &&
                        newScore > -1 &&
                        newScore <= flags?.who5ScoreThresholdForTherapy) ||
                    false;

                if (flags?.hidePhq8Gad7Assessments) {
                    assessments = qualifiesForTherapy
                        ? lowWellbeingAssessmentsOld
                        : highWellbeingAssessmentsOld;
                } else {
                    assessments = qualifiesForTherapy
                        ? lowWellbeingAssessments
                        : highWellbeingAssessments;
                }
            }
        }

        // no more assessments to show so we're submitting what we already have
        if (onlyWho5 || !assessments?.length) {
            onSubmit({ previousWho5: oldScore, who5: newScore });

            return;
        }

        // we have more assessments to show so retrieve questions that should be displayed
        retrieveQuestions(assessments);

        // save scores to use them in handleAssessmentsSubmit
        setScores({ previousWho5: oldScore, who5: newScore });
    };

    const handleAssessmentsSubmit = async (answers: MappedAnswers) => {
        const response = await dispatch.assessments.submitAssessments({
            answers,
            assessmentFlowUuid,
        });

        const data = {
            ...scores,
            ...response,
        };

        onSubmit(data as Scores);
    };

    return (
        <>
            {postWho5Questions.length ? (
                <Assessment
                    questions={postWho5Questions}
                    onSubmit={handleAssessmentsSubmit}
                    hideProgressBar
                />
            ) : (
                <Who5Assessment
                    onSubmit={handleWho5AssessmentSubmit}
                    assessmentFlowUuid={assessmentFlowUuid}
                    showTopControls={showTopControls}
                    currentWeek={currentWeekForLegacyPrograms}
                    hideEmploymentHealthQuestions={hideEmploymentHealthQuestions}
                />
            )}
        </>
    );
};
